import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import { getRandomColor, loadStatus } from "../util";

export const registerUser = createAsyncThunk(
  "user/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post("/register", userData);
      localStorage.setItem("token", response.data.token);

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const inviteRegisterUser = createAsyncThunk(
  "user/inviteRegisterUser",
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/invite-register/" + token, data);
      localStorage.setItem("token", response.data.token);

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post("/login", userData);
      localStorage.setItem("token", response.data.token);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getLoggedInUser = createAsyncThunk(
  "user/getLoggedInUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.get("/logged-in-user");
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.status === 404) {
          localStorage.removeItem("token");
        }
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "/user/forgotPassword",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.put("/forgot-password", userData);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const changePassword = createAsyncThunk(
  "/user/changePassword",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post("/users/change-password/" + id, data);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const newPasswordToken = createAsyncThunk(
  "/user/newPasswordToken",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put("/new-password/" + id, data);
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    userInfo: {},
    loading: false,
    isAuthenticated: false,
    roles: [],
    permissions: [],
    error: null,
    color: "bg-gray-400",
    load: {
      registerUser: false,
      inviteRegisterUser: false,
      loginUser: false,
      getLoggedInUser: false,
      forgotPassword: false,
      changePassword: false,
      newPasswordToken: false,
    },
  },
  reducers: {
    setUser: (state, action) => {
      state.isAuthenticated = true;
      state.roles = [action.payload.roles];
      delete action.payload.roles;
      state.permissions = action.payload.permissions;
      delete action.payload.permissions;
      state.userInfo = action.payload;
    },
    logout: (state) => {
      state.userInfo = {};
      state.isAuthenticated = false;
      state.roles = null;
      state.permissions = null;
      localStorage.removeItem("token");
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      //Register User
      .addCase(registerUser.pending, (state) => {
        state.load.registerUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.load.registerUser = loadStatus.success;
        state.isAuthenticated = true;
        state.roles = [action.payload.roles];
        delete action.payload.roles;
        state.permissions = action.payload.permissions;
        delete action.payload.permissions;
        state.userInfo = action.payload;
        state.color = getRandomColor();
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.load.registerUser = loadStatus.error;
        state.error = action.payload;
      })
      //invited User Registering
      .addCase(inviteRegisterUser.pending, (state) => {
        state.load.inviteRegisterUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(inviteRegisterUser.fulfilled, (state, action) => {
        state.load.inviteRegisterUser = loadStatus.success;
        state.isAuthenticated = true;
        state.roles = action.payload.roles;
        delete action.payload.roles;
        state.permissions = action.payload.permissions;
        delete action.payload.permissions;
        state.userInfo = action.payload;
        state.color = getRandomColor();
      })
      .addCase(inviteRegisterUser.rejected, (state, action) => {
        state.load.inviteRegisterUser = loadStatus.error;
        state.error = action.payload;
      })
      //Get Logged in ater refresh
      .addCase(getLoggedInUser.fulfilled, (state, action) => {
        state.load.getLoggedInUser = loadStatus.success;
        state.isAuthenticated = true;
        state.roles = action.payload.roles;
        // delete action.payload.roles;
        state.permissions = action.payload.permissions;
        delete action.payload.permissions;
        state.userInfo = action.payload;
        state.color = getRandomColor();
      })
      .addCase(getLoggedInUser.pending, (state) => {
        state.load.getLoggedInUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(getLoggedInUser.rejected, (state, action) => {
        state.load.getLoggedInUser = loadStatus.error;
        state.error = action.payload;
      })
      //Change Password of myself
      .addCase(changePassword.fulfilled, (state, action) => {
        state.load.changePassword = loadStatus.success;
      })
      .addCase(changePassword.pending, (state) => {
        state.load.changePassword = loadStatus.loading;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.load.changePassword = loadStatus.error;
        state.error = action.payload;
      })
      //Change Password with Reset Token
      .addCase(newPasswordToken.fulfilled, (state, action) => {
        state.load.newPasswordToken = loadStatus.success;
      })
      .addCase(newPasswordToken.pending, (state) => {
        state.load.newPasswordToken = loadStatus.loading;
        state.error = null;
      })
      .addCase(newPasswordToken.rejected, (state, action) => {
        state.load.newPasswordToken = loadStatus.error;
        state.error = action.payload;
      })
      // Logged in from Start
      .addCase(loginUser.fulfilled, (state, action) => {
        state.load.loginUser = loadStatus.success;
        state.isAuthenticated = true;
        state.roles = [action.payload.roles];
        delete action.payload.roles;
        state.permissions = action.payload.permissions;
        delete action.payload.permissions;
        state.userInfo = action.payload;
        state.color = getRandomColor();
      })
      .addCase(loginUser.pending, (state) => {
        state.load.loginUser = loadStatus.loading;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.load.loginUser = loadStatus.error;
        state.error = action.payload;
      })
      // Logged in from Start
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.load.forgotPassword = loadStatus.success;
        state.isAuthenticated = false;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.load.forgotPassword = loadStatus.loading;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.load.forgotPassword = loadStatus.error;
        state.error = action.payload;
      });
  },
});

export const { setUser, logout, clearError } = userSlice.actions;

export default userSlice.reducer;
